import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Video, ChevronRight, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Compact calendar peek: shows the next 3 upcoming meetings in the chat sidebar
 * so employees can see & join without leaving the chat context.
 */
export default function CalendarPeek() {
  const { api } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/meetings?range_days=14&include_past=false');
        if (cancelled) return;
        const upcoming = (res.data.meetings || [])
          .filter((m) => new Date(m.scheduled_at).getTime() >= Date.now() - 15 * 60 * 1000)
          .slice(0, 3);
        setMeetings(upcoming);
      } catch (err) { void err; }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const formatWhen = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return `Today · ${time}`;
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow · ${time}`;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
  };

  return (
    <div className="bg-white rounded-lg border" data-testid="calendar-peek">
      <div className="p-3 border-b flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
          <CalendarIcon className="w-3 h-3" /> Upcoming
        </p>
        <Link to="/team/meetings" className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid="calendar-peek-open">
          Open <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="p-2">
        {loading ? (
          <p className="text-xs text-slate-400 py-2 text-center">Loading…</p>
        ) : meetings.length === 0 ? (
          <Link to="/team/meetings" className="text-xs text-slate-500 py-3 block text-center hover:text-emerald-600">
            No upcoming meetings. Schedule one →
          </Link>
        ) : meetings.map((m) => (
          <a
            key={m.id}
            href={m.meet_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block p-2 rounded hover:bg-slate-50 group"
            data-testid={`calendar-peek-meeting-${m.id}`}
          >
            <div className="flex items-start gap-2">
              <Video className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate group-hover:text-emerald-700">{m.title}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatWhen(m.scheduled_at)} · {m.duration_min}m
                </p>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
